import React, { useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';

// Material UI
import Fab from '@mui/material/Fab';
import { Paper, Typography, Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Icons
import VerticalAlignTopOutlinedIcon from '@mui/icons-material/VerticalAlignTopOutlined';
import VerticalAlignBottomOutlinedIcon from '@mui/icons-material/VerticalAlignBottomOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';

// Components
import { DataGrid, FabContainer } from '@arredo/reactjs-ui';
import MovementFilters from "./filters";
import CreateEgressModal from './modals/createEgress';
import DetailEgressModal from './modals/detailEgress';
import CreateIngressModal from './modals/createIngress';
import Spinner from "../../../components/spinner";

// Hooks
import useCashRegisterMovements from '../../../hooks/cashRegisterMovements/useCashRegisterMovements';
import useCashRegisters from "../../../hooks/cashRegisters/useCashRegisters";
import useAuthorize from '../../../hooks/user/useAuthorize';
import useUser from '../../../hooks/user/useUser';

// Models
import { title, subTypes, movementTypesFormat, subTypesFormat, cashTypesFormat } from '../../../models/CashRegisterMovementEntity';
import { Permissions } from '../../../models/Roles';

// Others
import useStyles from './styles';

const columns = [
    {
        field: 'id',
        header: 'ID',
        sizes: { xs: 1 }
    },
    {
        field: 'type',
        header: 'Tipo',
        sizes: { xs: 1 },
        translate: (type) => movementTypesFormat[type] ?? type
    },
    {
        field: 'subType',
        header: 'Sub Tipo',
        sizes: { xs: 1 },
        translate: (type) => subTypesFormat[type] ?? type
    },
    {
        field: 'cashType',
        header: 'Medio de Pago',
        sizes: { xs: 2 },
        translate: (type) => cashTypesFormat[type] ?? type
    },
    {
        field: 'currency',
        header: 'Moneda',
        sizes: { xs: 1 }
    },
    {
        field: 'amount',
        header: 'Monto',
        type: 'money',
        sizes: { xs: 1 }
    },
    {
        field: 'userEmail',
        header: 'Email',
        sizes: { xs: 2 }
    },
    {
        field: 'created',
        header: 'Fecha de creación',
        type: 'date',
        sizes: { xs: 2 }
    }
]

const CashRegisterMovements = () => {
    const isAuthorized = useAuthorize();
    const { cashRegisters } = useCashRegisters({ isImprest: false });
    const { classes } = useStyles();
    const navigate = useNavigate();
    const [createEgressModalOpen, setCreateEgressModalOpen] = useState(false);
    const [detailEgressModalOpen, setDetailEgressModalOpen] = useState(false);
    const [createIngressModalOpen, setCreateIngressModalOpen] = useState(false);
    const [cashRegisterMovement, setCashRegisterMovement] = useState({});
    const { cashRegisterMovements, loading, loadingPrint, totalPage, filters, setParameterSearch, clearFilters, getDepositReceipt } = useCashRegisterMovements();
    const { business } = useUser();

    const GetCurrencies = useCallback(() => {
        const options = [{ name: 'Todos', id: '-1' }];

        if (business && business.currencies) {
            const currencies = business?.currencies.map((e) => { return { name: e, id: e } });
            return [...options, ...currencies]
        }

        return options;
    }, [business])

    const handleChangePage = (e, page) => {
        setParameterSearch({
            ...filters,
            pageNumber: Number(page)
        });
    }

    const handleFilterChange = (event) => {
        const value = event.target.value !== '-1'
            ? event.target.value
            : '';

        setParameterSearch({
            ...filters,
            [event.target.name]: value,
            pageNumber: 1
        });
    }

    const handleSaveCreateEgress = () => {
        setCreateEgressModalOpen(false);
        window.location.reload()
    }

    const handleSaveCreateIngress = () => {
        setCreateIngressModalOpen(false);
        window.location.reload()
    }

    const handleViewCashRegisterMovement = (item) => {
        setCashRegisterMovement(item);
        setDetailEgressModalOpen(true);
    }

    const refreshList = () => {
        setParameterSearch({
            ...filters
        });
    }

    return (
        <>
            {loadingPrint &&
                <Spinner lockScreen />}
            <Grid size="grow">
                <Paper className={classes.paper}>
                    <Typography variant="h4">
                        {title}
                    </Typography>
                </Paper>
            </Grid>
            {
                createEgressModalOpen &&
                <CreateEgressModal
                    open={createEgressModalOpen}
                    onClose={() => setCreateEgressModalOpen(false)}
                    onSave={handleSaveCreateEgress}
                />
            }
            {
                detailEgressModalOpen &&
                <DetailEgressModal
                    open={detailEgressModalOpen}
                    onClose={() => setDetailEgressModalOpen(false)}
                    cashRegisterMovement={cashRegisterMovement}
                    setCashRegisterMovement={setCashRegisterMovement}
                    onSave={refreshList}
                    currencies={GetCurrencies()}
                />
            }
            {
                createIngressModalOpen &&
                <CreateIngressModal
                    open={createIngressModalOpen}
                    onClose={() => setCreateIngressModalOpen(false)}
                    onSave={handleSaveCreateIngress}
                />
            }
            <MovementFilters
                filters={filters}
                clearFilters={clearFilters}
                onFilterChange={handleFilterChange}
                cashRegisters={cashRegisters}
                currencies={GetCurrencies()}
            />
            <DataGrid
                columns={columns}
                rows={cashRegisterMovements}
                loading={loading}
                paginationProps={{
                    count: totalPage,
                    page: filters?.pageNumber || 1,
                    onChange: handleChangePage
                }}
                actions={({ row }) => (
                    <>
                        {
                            (row?.subType === subTypes.Sale ||
                                row?.subType === subTypes.Void ||
                                row?.subType === subTypes.Refund) &&
                            <Tooltip title='Ver orden' aria-label='ver'>
                                <IconButton
                                    color='primary'
                                    onClick={() => navigate(`/orders/detail/${row.reference}`)}
                                    size="large"
                                >
                                    <ReceiptIcon />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            (row?.subType === subTypes.Deposit ||
                                row?.subType === subTypes.Imprest ||
                                row?.subType === subTypes.Adjustment) &&
                            <Tooltip title='Ver' aria-label='ver'>
                                <IconButton onClick={() => handleViewCashRegisterMovement(row)} size="large">
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            row?.subType === subTypes.Deposit &&
                            <Tooltip title='Imprimir' aria-label='Imprimir'>
                                <IconButton color='primary' onClick={() => getDepositReceipt(row.id)} size="large">
                                    <PrintIcon />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            row?.fileUrl &&
                            <a href={row.fileUrl} target='_blank' rel="noreferrer">
                                <IconButton color="primary" size="large">
                                    <AttachFileIcon />
                                </IconButton>
                            </a>
                        }
                    </>
                )}
            />
            {isAuthorized([Permissions.CashregisterCreate]) &&
                <FabContainer>
                    <Fab
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        onClick={() => setCreateEgressModalOpen(true)}
                    >
                        <VerticalAlignTopOutlinedIcon className={classes.extendedIcon} />
                        Egreso
                    </Fab>
                    <Fab
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        onClick={() => setCreateIngressModalOpen(true)}
                    >
                        <VerticalAlignBottomOutlinedIcon className={classes.extendedIcon} />
                        Ingreso
                    </Fab>
                </FabContainer>}
        </>
    );
}

export default CashRegisterMovements;